import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { inspectionsAPI } from "../../../services/api";
import { useProjectContext } from "../../../hooks/useProjectContext";

const titleCase = (value) =>
  (value || "-")
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

function AnswerModal({ item, onClose, onSubmit, loading }) {
  const [text, setText] = useState("");
  useEffect(() => {
    setText("");
  }, [item]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Engineering response</p>
            <h3 className="mt-1 font-semibold text-gray-900">
              {item.finding_number || `Issue #${item.finding_id}`}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div className="rounded-xl border bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Escalation reason</div>
            <p className="mt-1">{item.reason}</p>
          </div>
          <textarea
            rows={6}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Provide technical insight or resolution direction..."
            className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={loading || !text.trim()}
            onClick={() => onSubmit({ response_text: text.trim() })}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {loading ? <LoadingSpinner size="small" /> : <Send className="h-4 w-4" />} Submit Insight
          </button>
        </div>
      </div>
    </div>
  );
}

function ForwardModal({ item, targets, targetsLoading, loading, onClose, onSubmit }) {
  const [targetUserId, setTargetUserId] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    setTargetUserId("");
    setReason("");
  }, [item]);
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b p-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Escalate further</p>
            <h3 className="mt-1 font-semibold text-gray-900">
              {item.finding_number || `Issue #${item.finding_id}`}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 p-4">
          {targetsLoading ? (
            <div className="flex justify-center p-6"><LoadingSpinner size="small" /></div>
          ) : targets.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No higher escalation target exists in the published organization chart.
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Escalate To *</span>
                <select
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="">Select a person</option>
                  {targets.map((target) => (
                    <option key={target.user_id} value={target.user_id}>
                      {target.name} — {titleCase(target.role)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Reason *</span>
                <textarea
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-3"
                  placeholder="Explain why this escalation needs higher-level input..."
                />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={loading || targetsLoading || !targetUserId || !reason.trim()}
            onClick={() => onSubmit({ escalated_to_user_id: Number(targetUserId), reason: reason.trim() })}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
          >
            {loading ? <LoadingSpinner size="small" /> : <ArrowUpRight className="h-4 w-4" />} Escalate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EngineeringIssuesView() {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [answerItem, setAnswerItem] = useState(null);
  const [forwardItem, setForwardItem] = useState(null);
  const [forwardTargets, setForwardTargets] = useState([]);
  const [forwardTargetsLoading, setForwardTargetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return [];
    setLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.listEngineeringIssues(projectId);
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
      return data;
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load engineering issues.");
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    total: items.length,
    open: items.filter((i) => i.status === "open").length,
    answered: items.filter((i) => i.status === "answered").length,
  }), [items]);

  const submitAnswer = async (payload) => {
    if (!answerItem) return;
    setSaving(true);
    setError("");
    try {
      await inspectionsAPI.answerFindingEscalation(projectId, answerItem.id, payload);
      setAnswerItem(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit escalation response.");
    } finally {
      setSaving(false);
    }
  };

  const openForward = async (item) => {
    setForwardItem(item);
    setForwardTargets([]);
    setForwardTargetsLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.getFindingEscalationTargets(projectId, item.finding_id);
      setForwardTargets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load escalation targets.");
    } finally {
      setForwardTargetsLoading(false);
    }
  };

  const submitForward = async (payload) => {
    if (!forwardItem) return;
    setSaving(true);
    setError("");
    try {
      await inspectionsAPI.forwardFindingEscalation(projectId, forwardItem.id, payload);
      setForwardItem(null);
      setForwardTargets([]);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to escalate the issue further.");
    } finally {
      setSaving(false);
    }
  };

  const openFile = async (_item, file) => {
    if (!file?.id) return;
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.opener = null;
      previewWindow.document.title = file.name || "Opening file";
      previewWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#374151">Opening file…</div>';
    }
    try {
      const res = await inspectionsAPI.getDocumentDownloadUrl(projectId, file.id);
      const freshUrl = res?.data?.url;
      if (!freshUrl) throw new Error("A fresh file link could not be generated.");
      if (previewWindow && !previewWindow.closed) previewWindow.location.replace(freshUrl);
      else window.location.assign(freshUrl);
    } catch (err) {
      if (previewWindow && !previewWindow.closed) previewWindow.close();
      setError(err?.response?.data?.detail || err?.message || "Failed to open file.");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700"><Activity className="h-3.5 w-3.5" /> Project Engineering</div>
          <h2 className="mt-2 text-2xl font-bold text-gray-900">Engineering Issues</h2>
          <p className="text-sm text-gray-500">Escalations sent directly to you. Respond here or escalate further to the Supervisor.</p>
        </div>
        <button type="button" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border p-4"><p className="text-xs uppercase text-gray-500">Total escalations</p><p className="mt-2 text-3xl font-bold">{counts.total}</p></div>
        <div className="rounded-2xl border p-4"><p className="text-xs uppercase text-gray-500">Awaiting input</p><p className="mt-2 text-3xl font-bold text-amber-700">{counts.open}</p></div>
        <div className="rounded-2xl border p-4"><p className="text-xs uppercase text-gray-500">Responded</p><p className="mt-2 text-3xl font-bold text-emerald-700">{counts.answered}</p></div>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
      {loading ? (
        <div className="flex items-center justify-center rounded-xl border p-10"><LoadingSpinner /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-gray-500">No engineering escalations are assigned to you.</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{item.finding_number || `Issue #${item.finding_id}`}</h3>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item.status === "open" ? "bg-amber-50 text-amber-700" : item.status === "answered" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>{titleCase(item.status)}</span>
                  </div>
                  <p className="mt-2 text-sm text-gray-700">{item.description}</p>
                  <div className="mt-3 rounded-xl bg-gray-50 p-3 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Escalation reason</p>
                    <p className="mt-1 text-gray-800">{item.reason}</p>
                  </div>
                  {item.files?.length ? (
                    <div className="mt-3 rounded-xl border p-3">
                      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900"><FileText className="h-4 w-4 text-orange-600" /> Related Files</div>
                      <div className="max-h-40 space-y-1.5 overflow-y-auto">
                        {item.files.map((file) => (
                          <button key={file.id} type="button" onClick={() => openFile(item, file)} className="flex w-full items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left text-sm hover:border-orange-200 hover:bg-orange-50">
                            <span className="truncate">{file.name || `File #${file.id}`}</span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-orange-700" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {item.upstream_response_text ? (
                    <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
                      <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Supervisor Response</div>
                      <p className="mt-1 whitespace-pre-wrap">{item.upstream_response_text}</p>
                      {item.upstream_answered_by_name ? <p className="mt-1 text-xs text-blue-700">From: {item.upstream_answered_by_name}</p> : null}
                    </div>
                  ) : null}

                  {item.response_text ? (
                    <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">
                      <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Engineering insight</div>
                      <p className="mt-1 whitespace-pre-wrap">{item.response_text}</p>
                    </div>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                    <span>From: {item.escalated_by_name || "—"}</span><span>Severity: {item.severity || "—"}</span><span>Due: {item.due_date || "—"}</span>
                  </div>
                </div>
                {["open", "forwarded"].includes(item.status) ? (
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button type="button" onClick={() => setAnswerItem(item)} className="inline-flex items-center gap-2 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600"><Clock3 className="h-4 w-4" /> {item.status === "forwarded" ? "Respond Back" : "Provide Insight"}</button>
                    {item.status === "open" ? (
                      <button type="button" onClick={() => openForward(item)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"><ArrowUpRight className="h-4 w-4" /> Escalate Further</button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <AnswerModal
        item={answerItem}
        onClose={() => setAnswerItem(null)}
        onSubmit={submitAnswer}
        loading={saving}
      />
      <ForwardModal item={forwardItem} targets={forwardTargets} targetsLoading={forwardTargetsLoading} loading={saving} onClose={() => setForwardItem(null)} onSubmit={submitForward} />
    </div>
  );
}
